import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { CreateRecurringEntryInput } from '@accounting/shared';
import {
  useDeleteRecurringEntry,
  useRecurringEntry,
  useRunRecurringEntry,
  useSetRecurringActive,
  useUpdateRecurringEntry,
} from '../../hooks/useRecurring';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { RecurringEntryForm } from '../../components/recurring/RecurringEntryForm';
import { ApiError } from '../../lib/api';
import { formatAccountingDate } from '../../lib/formatters';
import { frequencyLabel } from './recurring.index';

export const Route = createFileRoute('/_auth/recurring/$id')({
  component: RecurringDetailPage,
});

function RecurringDetailPage() {
  const { id } = Route.useParams();
  const companyId = useActiveCompanyId();
  const navigate = useNavigate();
  const { data: entry, isLoading, error } = useRecurringEntry(companyId, id);
  const update = useUpdateRecurringEntry(companyId);
  const run = useRunRecurringEntry(companyId);
  const setActive = useSetRecurringActive(companyId);
  const del = useDeleteRecurringEntry(companyId);

  if (!companyId) {
    return <div className="card" style={{ textAlign: 'center', padding: 32 }}><p className="muted">No active company.</p></div>;
  }
  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !entry) return <p className="field-error">Failed to load recurring entry.</p>;

  const updateError =
    update.error instanceof ApiError
      ? (update.error.body as { error?: string } | null)?.error ?? 'Update failed'
      : update.error?.message;
  const runError =
    run.error instanceof ApiError
      ? (run.error.body as { error?: string } | null)?.error ?? 'Run failed'
      : run.error?.message;

  const submit = (input: CreateRecurringEntryInput) => update.mutate({ id, input });

  return (
    <div>
      <Link to="/recurring" style={{ fontSize: 13 }}>← Recurring</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>
          {entry.name} {entry.isActive ? <span className="tag ok">active</span> : <span className="tag">paused</span>}
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {entry.isActive && (
            <button className="btn-primary" disabled={run.isPending} onClick={() => run.mutate(id)} title="Generate the next scheduled entry now">Run now</button>
          )}
          <button className="btn-secondary" disabled={setActive.isPending} onClick={() => setActive.mutate({ id, isActive: !entry.isActive })}>
            {entry.isActive ? 'Pause' : 'Resume'}
          </button>
          <button
            className="btn-danger"
            disabled={del.isPending}
            onClick={() => {
              if (confirm('Delete this template? Already-generated journal entries are kept.')) {
                del.mutate(id, { onSuccess: () => void navigate({ to: '/recurring' }) });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 16, maxWidth: 760 }}>
        <div className="form-grid" style={{ marginBottom: 0 }}>
          <span className="muted">Schedule</span><span>{frequencyLabel(entry)}</span>
          <span className="muted">Next run</span><span>{formatAccountingDate(entry.nextRunDate)}{entry.isDue && entry.isActive ? ' (due)' : ''}</span>
          <span className="muted">Runs so far</span><span>{entry.runCount}{entry.maxRuns != null ? ` of ${entry.maxRuns}` : ''}</span>
          {entry.lastRunAt && (<><span className="muted">Last run</span><span>{new Date(entry.lastRunAt).toLocaleString()}</span></>)}
          {entry.endDate && (<><span className="muted">Ends</span><span>{formatAccountingDate(entry.endDate)}</span></>)}
        </div>
        {entry.lastError && <div className="field-error" style={{ marginTop: 10 }}>Last run failed: {entry.lastError}</div>}
      </div>

      {runError && <div className="field-error" style={{ marginBottom: 12 }}>{runError}</div>}

      <h2 style={{ fontSize: 16, margin: '0 0 12px' }}>Edit template</h2>
      <RecurringEntryForm
        key={entry.updatedAt}
        companyId={companyId}
        initial={entry}
        submitLabel="Save changes"
        pending={update.isPending}
        errorMessage={updateError}
        onSubmit={submit}
      />
    </div>
  );
}
